/** Main-thread handle to the io worker (lazy singleton, Comlink RPC). */

import * as Comlink from 'comlink';

import type { MeshData } from '@core/geometry/types';
import type { ExportFormat, IoWorkerApi } from './io.worker';

let worker: Comlink.Remote<IoWorkerApi> | null = null;

function getWorker(): Comlink.Remote<IoWorkerApi> {
  if (!worker) {
    worker = Comlink.wrap<IoWorkerApi>(
      new Worker(new URL('./io.worker.ts', import.meta.url), { type: 'module' }),
    );
  }
  return worker;
}

export async function parseMeshFile(name: string, buffer: ArrayBuffer): Promise<MeshData> {
  return getWorker().parseMesh(name, Comlink.transfer(buffer, [buffer]));
}

export async function exportMeshes(
  format: ExportFormat,
  meshes: MeshData[],
): Promise<Blob> {
  // Clone body buffers — the originals are live in the viewport/evaluator.
  const copies = meshes.map((m) => ({
    positions: m.positions.slice(),
    normals: m.normals.slice(),
    indices: m.indices.slice(),
    edgePositions: new Float32Array(0),
  }));
  const transfers = copies.flatMap((m) => [
    m.positions.buffer,
    m.normals.buffer,
    m.indices.buffer,
    m.edgePositions.buffer,
  ]);
  const result = await getWorker().exportMeshes(format, Comlink.transfer(copies, transfers));
  return new Blob([result.data], { type: result.mime });
}

export type { ExportFormat };
